import cloneDeep from 'lodash/cloneDeep';
import max from 'lodash/max';
import min from 'lodash/min';
import { Layer as ParseLayer } from '@meitu-graphic/ag-psd';
import { revertPointY, rotatePoint, translatePointMove } from '@meitu/saas-editor-core/es/utils/math-kit';
import { DefaultClientGroupLayer } from '@meitu/saas-editor-core/es/version-parse/constants';
import { ILayerProps, IPoint } from '@meitu/saas-editor-core/es/version-types';
import { traverseTree } from '@meitu/saas-editor-core/utils/tree-kit';
import { PsdParseWarnType, UploadImage, WarnOrErrorCallbackFunc } from './types';
import { setCutMaskLayers } from './utils';

/**
 * @description 获取所有子图层的基础信息
 * @param layers
 * @returns
 */
export const getAllChildrenBoxInfo = (layers: ILayerProps[]) => {
    const boxInfos = [] as any[];
    for (let i = 0, len = layers.length; i < len; i++) {
        const { left, top, width, height, rotate } = layers[i];

        boxInfos.push({
            left,
            top,
            width: width,
            height: height,
            rotate: rotate || 0,
        });
    }
    return boxInfos;
};

export interface BoxInfo {
    left: number;
    top: number;
    width: number;
    height: number;
    rotate: number;
}

/**
 * @description 获取矩形旋转后的顶点坐标
 * @param boxInfo 图层基础数据
 * @param [isClassify] 是否归类 默认false
 * @returns number[][] | Point[]
 * @example getVertexPoint(boxInfo) 返回 [{x1,y2},{x2,y2}...]
 * @example getVertexPoint(boxInfo,true) 返回 [[x1,x2,..],[y1,y2,...]]
 */
export const getVertexPoint = (boxInfo: BoxInfo, isClassify = false): number[][] | IPoint[] => {
    let vertexPoint: IPoint[] = [];
    const classify: number[][] = [[], []];
    const newVertexPoint: IPoint[] = [];
    const { left, top, width, height, rotate } = boxInfo;
    vertexPoint = [
        {
            x: left,
            y: top,
        },
        {
            x: left,
            y: top + height,
        },
        {
            x: left + width,
            y: top,
        },
        {
            x: left + width,
            y: top + height,
        },
    ];

    const rotateVertexPoint = [
        {
            x: -width / 2,
            y: -height / 2,
        },
        {
            x: -width / 2,
            y: height / 2,
        },
        {
            x: width / 2,
            y: height / 2,
        },
        {
            x: width / 2,
            y: -height / 2,
        },
    ];
    // 计算旋转后的角度
    vertexPoint.forEach((v: IPoint, i) => {
        let { x, y } = v;

        if (rotate !== 0) {
            const { x: newX, y: newY } = translatePointMove(
                revertPointY(rotatePoint(rotateVertexPoint[i], rotate)),
                left + width / 2,
                top + height / 2,
            );
            x = newX;
            y = newY;
        }

        if (isClassify) {
            classify[0].push(x);
            classify[1].push(y);
        } else {
            newVertexPoint.push({ x, y });
        }
    });
    return isClassify ? classify : newVertexPoint;
};

export const setGroupSize = async (clientLayers: ILayerProps[], parentLayer: ILayerProps) => {
    const child = clientLayers.filter((clientLayer) => clientLayer.parentId === parentLayer.id);

    if (child.length === 0) {
        return;
    }

    const boxInfos = getAllChildrenBoxInfo(child);

    const pointsArr: number[][] = [];

    // 收集所有的X、Y坐标
    boxInfos.forEach((v, i) => {
        const [xPoints, yPoints] = getVertexPoint(v, true) as number[][];
        pointsArr[0] = xPoints.concat(pointsArr[0]);
        pointsArr[1] = yPoints.concat(pointsArr[1]);
    });
    // x最小值就是left 坐标
    const left = min(pointsArr[0]) || 0;
    // width = maxX- left
    const width = (max(pointsArr[0]) || 0) - left;
    // y最小值就是top 坐标
    const top = min(pointsArr[1]) || 0;
    // height = maxY- top
    const height = (max(pointsArr[1]) || 0) - top;
    // 包围盒数据
    parentLayer.left = left;
    parentLayer.top = top;
    parentLayer.width = width;
    parentLayer.height = height;
};

export const getGroupLayer = async (
    stageWidth: number,
    stageHeight: number,
    clientLayers: ILayerProps[],
    parentLayer: ParseLayer,
    layer: ParseLayer,
    uploadFile: UploadImage,
    warnOrErrorCallback?: WarnOrErrorCallbackFunc,
    parseNoLimit = false,
    parseLayerCallback?: (originLayer: ParseLayer, resultLayer: ILayerProps) => Promise<ILayerProps | null>,
) => {
    const groupLayer = cloneDeep(DefaultClientGroupLayer);
    groupLayer.opacity = layer.opacity || 1;
    groupLayer.id = layer.id?.toString() || groupLayer.id;

    groupLayer.title = layer.name || '';
    groupLayer.parentId = parentLayer.id && parentLayer.id === layer.id ? '' : parentLayer.id?.toString();
    warnOrErrorCallback && warnOrErrorCallback(`解析图层组：${groupLayer.title}`, PsdParseWarnType.Info);
    if (!layer.children || layer.children?.length === 0) {
        if (parseNoLimit) {
            return null;
        }
        warnOrErrorCallback &&
            !parseNoLimit &&
            warnOrErrorCallback(`图层${groupLayer.title}：没有子图层,是个无效图层`, PsdParseWarnType.Error);
        return groupLayer;
    }

    await setCutMaskLayers(
        stageWidth,
        stageHeight,
        clientLayers,
        layer.children,
        uploadFile,
        warnOrErrorCallback,
        parseNoLimit,
    );

    if (parseLayerCallback) {
        const newLayer = await parseLayerCallback(layer, groupLayer);
        return newLayer;
    }
    return groupLayer;
};

export const recalculateGroupSize = async (clientLayers: ILayerProps[]) => {
    for (let i = 0; i < clientLayers.length; i++) {
        const clientLayer = clientLayers[i];
        await setGroupSize(clientLayers, clientLayer);
    }
};
